import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { downloadInvoicePDF } from '../utils/pdfGenerator';
import { formatCurrency } from '../utils/formatters';
import { useAuth, ROLES } from '../context/AuthContext';

const InvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const isOwner = hasRole(ROLES.BUSINESS_OWNER);
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLink, setPaymentLink] = useState(null);

  useEffect(() => {
    fetchInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const invoiceRes = await apiClient.get(`/invoices/${id}`);
      const invoiceData = invoiceRes.data;
      setInvoice(invoiceData);

      const clientRes = await apiClient.get(`/clients/${invoiceData.client_id}`);
      setClient(clientRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (invoice && client) {
      downloadInvoicePDF(invoice, client, {
        name: user.business_name || user.email,
        email: user.email
      });
    }
  };

  const handleMarkPaid = async () => {
    try {
      await apiClient.post(`/invoices/${id}/mark-paid`);
      await fetchInvoice();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
    }
  };

  const handleCreatePaymentLink = async () => {
    try {
      const res = await apiClient.post(`/payments/create-payment-link?invoice_id=${id}`);
      setPaymentLink(res.data.url);
    } catch (error) {
      console.error('Error creating payment link:', error);
    }
  };

  if (loading) {
    return <div className="p-8" data-testid="invoice-view-loading">Loading...</div>;
  }

  if (!invoice || !client) {
    return <div className="p-8" data-testid="invoice-not-found">Invoice not found</div>;
  }

  return (
    <div className="p-8" data-testid="invoice-view">
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/invoices')}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center"
            title="Back to Invoices"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="invoice-view-title">
            Invoice {invoice.invoice_number}
          </h1>
        </div>
        <div className="flex gap-2 items-center">
          {!isOwner && (
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded mr-2">View Only</span>
          )}
          {isOwner && (
            <button
              onClick={() => navigate(`/invoices/edit/${id}`)}
              data-testid="edit-invoice-button"
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Edit
            </button>
          )}
          <button
            onClick={handleDownloadPDF}
            data-testid="download-pdf-button"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Download PDF
          </button>
          {isOwner && invoice.status !== 'paid' && (
            <>
              <button
                onClick={handleMarkPaid}
                data-testid="mark-paid-button"
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Mark as Paid
              </button>
              <button
                onClick={handleCreatePaymentLink}
                data-testid="create-payment-link-button"
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Create Payment Link
              </button>
            </>
          )}
        </div>
      </div>

      {paymentLink && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6" data-testid="payment-link-banner">
          <p className="text-sm text-gray-700 mb-2">Payment Link:</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={paymentLink}
              readOnly
              data-testid="payment-link-input"
              className="flex-1 px-3 py-2 border border-gray-300 rounded bg-white"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(paymentLink);
                alert('Payment link copied to clipboard!');
              }}
              data-testid="copy-payment-link-button"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-8">
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">From:</h3>
            <p className="text-gray-900">{user.business_name || user.email}</p>
            <p className="text-gray-600">{user.email}</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Bill To:</h3>
            <p className="text-gray-900">{client.name}</p>
            {client.company && <p className="text-gray-600">{client.company}</p>}
            <p className="text-gray-600">{client.email}</p>
            {client.address && <p className="text-gray-600">{client.address}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <p className="text-sm text-gray-600">Issue Date</p>
            <p className="font-semibold" data-testid="issue-date">{invoice.issue_date}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Due Date</p>
            <p className="font-semibold" data-testid="due-date">{invoice.due_date}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Status</p>
            <span className={`inline-block px-3 py-1 text-sm rounded-full ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
              invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
              }`} data-testid="invoice-status">
              {invoice.status.toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-600">Currency</p>
            <p className="font-semibold" data-testid="currency">{invoice.currency}</p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-sm font-semibold text-gray-700">Description</th>
                <th className="text-right py-2 text-sm font-semibold text-gray-700">Qty</th>
                <th className="text-right py-2 text-sm font-semibold text-gray-700">Unit Price</th>
                <th className="text-right py-2 text-sm font-semibold text-gray-700">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-3 text-gray-900">{item.description}</td>
                  <td className="py-3 text-right text-gray-900">{item.quantity}</td>
                  <td className="py-3 text-right text-gray-900">{formatCurrency(item.unit_price, invoice.currency)}</td>
                  <td className="py-3 text-right text-gray-900">{formatCurrency(item.amount, invoice.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end">
          <div className="w-64 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <span className="text-gray-900" data-testid="view-subtotal">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tax ({invoice.tax}%):</span>
              <span className="text-gray-900" data-testid="view-tax">{formatCurrency(invoice.subtotal * invoice.tax / 100, invoice.currency)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
              <span>Total:</span>
              <span data-testid="view-total">{formatCurrency(invoice.total, invoice.currency)}</span>
            </div>
          </div>
        </div>

        {invoice.notes && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-gray-700 mb-2">Notes:</h3>
            <p className="text-gray-600" data-testid="invoice-notes">{invoice.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceView;